import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { collections } from '../data/mockData';
import OrientedImage, { parseSizeDimensions } from '../components/OrientedImage';
import { useLightbox } from '../hooks/useLightbox';
import { LightboxOverlay } from '../components/LightboxOverlay';

/* ── Helpers ── */
const getUrl = (artwork, isMobile) => (isMobile && artwork.urlMobile) ? artwork.urlMobile : artwork.url;
const getSize = (artwork, isMobile) => (isMobile && artwork.sizeMobile) ? artwork.sizeMobile : artwork.size;
const getStatus = () => null;

const GALLERY_SCALE = {
  'architecture-of-panic': 110,
  'calm-in-chaos': 95,
  default: 72,
};

/* ── Sub-components ── */

const CollectionNotFound = () => (
  <div className="min-h-screen flex items-center justify-center pt-14" data-testid="collection-not-found">
    <div className="text-center">
      <h2 className="text-2xl text-gray-900 mb-4 font-signature">Collection not found</h2>
      <Link to="/" className="text-teal-600 hover:underline font-signature">Return home</Link>
    </div>
  </div>
);

const CollectionEmpty = ({ collection }) => (
  <div className="min-h-screen bg-white pt-14" data-testid="collection-empty">
    <div className="relative h-48 bg-gradient-to-br from-teal-50 to-orange-50 flex items-center justify-center">
      <div className="relative z-10 text-center px-8">
        <h1 className="text-5xl font-signature text-gray-900 mb-3">{collection.title}</h1>
        <p className="text-lg font-signature text-gray-600 max-w-2xl mx-auto">{collection.description}</p>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-8 py-16 text-center">
      <p className="text-lg text-gray-600 font-signature">Coming soon...</p>
    </div>
  </div>
);

const GalleryTile = ({ artwork, index, dims, maxW, scaleFactor, isMobile, onClick }) => {
  const { w: intW, h: intH } = dims[index];
  const scale = (intW / maxW) * scaleFactor;
  return (
    <div
      key={artwork.id}
      className="group relative cursor-pointer break-inside-avoid mb-4"
      onClick={onClick}
      data-testid={`gallery-tile-${index}`}
    >
      <div className="relative overflow-hidden rounded-lg mx-auto" style={{ aspectRatio: `${intW}/${intH}`, maxWidth: `${scale}%` }}>
        <OrientedImage src={getUrl(artwork, isMobile)} alt={artwork.name || artwork.title} intendedW={intW} intendedH={intH} loading="lazy" />
        <div className="absolute inset-0 bg-white/60 opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col items-center justify-end rounded-lg p-4 z-10">
          <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
            {artwork.name && (
              <h3 className="text-gray-800 text-xs font-signature text-center mb-0.5">
                {artwork.name}
                {getStatus(artwork) && <span className="text-gray-500 text-xs"> ({getStatus(artwork)})</span>}
              </h3>
            )}
            <p className="text-gray-500 text-xs font-signature text-center">
              {artwork.title}, {getSize(artwork, isMobile)}, {artwork.year}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ── */

const CollectionPage = () => {
  const { slug } = useParams();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [heroSlide, setHeroSlide] = useState(0);
  const [heroTick, setHeroTick] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const lb = useLightbox();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const collection = useMemo(
    () => Object.values(collections).find(col => col.slug === slug),
    [slug]
  );

  const carouselImages = useMemo(() => {
    if (!collection?.images) return [];
    if (collection.slug === 'architecture-of-panic') {
      return [...collection.images].sort((a, b) => {
        const aD = parseSizeDimensions(a.size);
        const bD = parseSizeDimensions(b.size);
        return (bD.w * bD.h) - (aD.w * aD.h);
      });
    }
    return collection.images;
  }, [collection]);

  useEffect(() => {
    if (!collection?.hero?.carousel || !carouselImages.length || heroPaused) return;
    const timer = setInterval(() => {
      setHeroSlide(prev => (prev + 1) % carouselImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [collection, carouselImages, heroPaused, heroTick]);

  const closeLightbox = useCallback(() => {
    setSelectedImageIndex(null);
    lb.resetZoom();
  }, [lb]);

  const goToPreviousImage = useCallback(() => {
    if (!collection) return;
    setSelectedImageIndex(prev => (prev === 0 ? collection.images.length - 1 : prev - 1));
    lb.resetZoom();
  }, [collection, lb]);

  const goToNextImage = useCallback(() => {
    if (!collection) return;
    setSelectedImageIndex(prev => (prev === collection.images.length - 1 ? 0 : prev + 1));
    lb.resetZoom();
  }, [collection, lb]);

  useEffect(() => {
    const onKey = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'ArrowLeft') goToPreviousImage();
      if (e.key === 'ArrowRight') goToNextImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedImageIndex, goToPreviousImage, goToNextImage, closeLightbox]);

  if (!collection) return <CollectionNotFound />;
  if (!collection.images?.length) return <CollectionEmpty collection={collection} />;

  const handleOverlayClick = () => {
    if (lb.shouldSuppressClick()) return;
    closeLightbox();
  };

  const dims = collection.images.map(a => parseSizeDimensions(a.size));
  const maxW = Math.max(...dims.map(d => d.w));
  const scaleFactor = GALLERY_SCALE[collection.slug] || GALLERY_SCALE.default;
  const isAoP = collection.slug === 'architecture-of-panic';
  const selectedArt = selectedImageIndex !== null ? collection.images[selectedImageIndex] : null;

  return (
    <div className="min-h-screen bg-white pt-14" data-testid="collection-page">
      {/* Hero section */}
      {collection.hero?.carousel ? (
        <>
          <div className="py-4 md:py-6 px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-signature text-gray-900 mb-2 tracking-wide" data-testid="collection-title">{collection.title}</h1>
            <div className="w-16 h-0.5 bg-gray-300 mx-auto mb-2" />
            <p className="text-sm md:text-base font-signature text-gray-700 max-w-2xl mx-auto leading-relaxed">{collection.description}</p>
          </div>
          <div className="relative w-full bg-white overflow-hidden group" style={{ height: '65vh' }} data-testid="collection-hero-carousel">
            <div className="flex h-full transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${heroSlide * 100}%)` }}>
              {carouselImages.map((artwork) => (
                <div key={artwork.id} className="flex-shrink-0 w-full h-full flex flex-col items-center justify-center px-8">
                  <div className="relative overflow-hidden cursor-pointer" style={{ height: 'calc(100% - 3.5rem)', maxWidth: '90%' }} onClick={() => setSelectedImageIndex(collection.images.findIndex(img => img.id === artwork.id))}>
                    <img src={getUrl(artwork, isMobile)} alt={artwork.name} className="h-full w-auto object-contain" style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.1))' }} />
                  </div>
                  <div className="mt-2 text-center">
                    {artwork.name && <h3 className="text-gray-800 text-sm font-signature">{artwork.name}</h3>}
                    <p className="text-gray-500 text-xs font-signature">{artwork.title}, {artwork.size}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => { setHeroSlide(prev => (prev === 0 ? carouselImages.length - 1 : prev - 1)); setHeroTick(t => t + 1); }} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10" data-testid="carousel-prev-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => { setHeroSlide(prev => (prev + 1) % carouselImages.length); setHeroTick(t => t + 1); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10" data-testid="carousel-next-btn">
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-2 right-4 flex items-center gap-2 z-10">
              <button onClick={() => setHeroPaused(prev => !prev)} className="text-gray-500 hover:text-gray-700 bg-white/80 hover:bg-white p-2 rounded-full transition-all duration-300" aria-label={heroPaused ? 'Play carousel' : 'Pause carousel'} data-testid="carousel-pause-btn">
                {heroPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </button>
              <span className="text-gray-500 font-signature text-xs bg-white/80 px-2 py-1 rounded-full" data-testid="carousel-counter">{heroSlide + 1} / {carouselImages.length}</span>
            </div>
          </div>
        </>
      ) : collection.hero?.banner ? (
        <div className="relative w-full">
          <img src={collection.hero.banner} alt={collection.title} className="w-full max-h-[70vh] object-cover" data-testid="collection-hero-banner" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end">
            <div className="px-8 pb-8 max-w-7xl mx-auto w-full">
              <h1 className="text-4xl md:text-5xl font-signature text-white mb-2" data-testid="collection-title">{collection.title}</h1>
              <p className="text-base md:text-lg font-signature text-white/80 max-w-2xl">{collection.description}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative py-12 bg-gradient-to-br from-teal-50 to-orange-50 flex items-center justify-center">
          <div className="relative z-10 text-center px-8">
            <h1 className="text-5xl font-signature text-gray-900 mb-2" data-testid="collection-title">{collection.title}</h1>
            <p className="text-lg font-signature text-gray-600 max-w-2xl mx-auto">{collection.description}</p>
          </div>
        </div>
      )}

      {/* Gallery */}
      <div className={`mx-auto px-6 py-4 md:py-6 ${isAoP ? 'max-w-7xl' : 'max-w-5xl'}`}>
        <div className={`columns-1 md:columns-2 ${isAoP ? 'gap-4' : 'gap-6'}`}>
          {collection.images.map((artwork, index) => (
            <GalleryTile
              key={artwork.id}
              artwork={artwork}
              index={index}
              dims={dims}
              maxW={maxW}
              scaleFactor={scaleFactor}
              isMobile={isMobile}
              onClick={() => setSelectedImageIndex(index)}
            />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedArt && (() => {
        const { w: lbW, h: lbH } = parseSizeDimensions(selectedArt.size);
        return (
          <LightboxOverlay
            onClose={closeLightbox}
            onPrev={goToPreviousImage}
            onNext={goToNextImage}
            currentIndex={selectedImageIndex}
            total={collection.images.length}
            zoomLevel={lb.zoomLevel}
            isDragging={lb.isDragging}
            onMouseMove={lb.handleMouseMove}
            onMouseUp={lb.handleMouseUp}
            onMouseLeave={lb.handleMouseUp}
            onOverlayClick={handleOverlayClick}
          >
            <div className="max-w-6xl w-full px-4 pb-16 overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div
                className="flex items-center justify-center"
                style={{ cursor: lb.getCursor() }}
                onMouseDown={lb.handleMouseDown}
                onTouchStart={lb.handleTouchStart}
                onTouchMove={lb.handleTouchMove}
                onTouchEnd={lb.handleTouchEnd}
              >
                <div
                  className="relative"
                  style={{
                    width: `min(100%, calc(80vh * ${lbW / lbH}))`,
                    maxHeight: '80vh',
                    aspectRatio: `${lbW}/${lbH}`,
                    overflow: 'hidden',
                    transform: lb.getTransform(),
                    transition: lb.isDragging ? 'none' : 'transform 0.3s ease',
                  }}
                  data-testid="lightbox-image"
                >
                  <OrientedImage src={getUrl(selectedArt, isMobile)} alt={selectedArt.name || selectedArt.title} intendedW={lbW} intendedH={lbH} draggable={false} className="w-full h-full object-cover select-none" />
                </div>
              </div>
              <div className="mt-3 text-center">
                {selectedArt.name && (
                  <h3 className="text-lg font-signature text-gray-900 mb-0.5">
                    {selectedArt.name}
                    {getStatus(selectedArt) && <span className="text-gray-400"> ({getStatus(selectedArt)})</span>}
                  </h3>
                )}
                <p className="text-sm font-signature text-gray-500">
                  {selectedArt.title}, {getSize(selectedArt, isMobile)}, {selectedArt.year}
                </p>
              </div>
            </div>
          </LightboxOverlay>
        );
      })()}
    </div>
  );
};

export default CollectionPage;
